/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useServices } from "appServices";
import { formatDateDDMMYYYY, formatDateYYYYMMDD } from "helpers/date";
import { addressDataToForm } from "helpers/forms/address";
import { type AddressData } from "services/address/addressTypes";
import { DirectorAddressKey } from "services/director/directorAddressStore";
import { DirectorCreateData, type DirectorData, DirectorUpdateData } from "services/director/directorTypes";
import {
  DirectorPosition,
  type DirectorPositionStore,
  isDirector,
  isShareholder,
} from "services/directorPosition/directorPositionStore";
import { type DirectorStepFormValues } from "./directorTypes";

const getDirectorPosition = (
  directorId: string | undefined,
  isMainDirector: boolean,
  directorPositionsStore: DirectorPositionStore,
): DirectorPosition | undefined => {
  if (isMainDirector) {
    return DirectorPosition.DirectorAndShareholder;
  }

  if (!directorId) {
    return undefined;
  }

  return directorPositionsStore.getForDirector(directorId);
};

export const isCountry = (selectedCountry?: string, countryCheckList?: string[]): boolean => {
  if (
    countryCheckList?.some(
      (countryToCheck) => selectedCountry?.toLocaleLowerCase() === countryToCheck?.toLocaleLowerCase(),
    )
  ) {
    return true;
  }
  return false;
};

const directorDataToDirectorForm = (
  director?: DirectorData,
  address?: AddressData,
  previousAddress?: AddressData,
  position?: DirectorPosition,
): DirectorStepFormValues => {
  return {
    firstName: director?.firstName ?? undefined,
    middleNames: director?.middleNames ?? undefined,
    lastName: director?.lastName ?? undefined,
    dob: director?.dob ? formatDateDDMMYYYY(director.dob) : undefined,
    nationality: director?.nationality ?? undefined,
    address: addressDataToForm(address),
    isCurrentAddressLessThanOneYear: director?.isCurrentAddressLessThanOneYear ?? !!previousAddress,
    previousAddressInUk: director?.previousAddressInUk ?? (previousAddress ? true : undefined),
    previousAddress: addressDataToForm(previousAddress),
    position,
    countryOfBirth: director?.countryOfBirth ?? undefined,
    suburbOfBirth: director?.suburbOfBirth ?? undefined,
    stateOfBirth: director?.stateOfBirth ?? undefined,
    townOfBirth: director?.townOfBirth ?? undefined,
    tfn: director?.tfn ?? undefined,
    email: director?.email ?? undefined,
    mobile: director?.mobile ?? undefined,
    chPersonalCode: director?.chPersonalCode ?? undefined,
    chPersonalCodeExist: !!director?.chPersonalCode,
    chPersonalCodeAcknowledge: director?.firstName ? !!director?.chPersonalCode : false,
  };
};

export const directorFormToDirectorCreateData = (
  companyId: string,
  directorForm: DirectorStepFormValues,
): DirectorCreateData => {
  return new DirectorCreateData({
    companyId,
    firstName: directorForm.firstName || undefined,
    middleNames: directorForm.middleNames || undefined,
    lastName: directorForm.lastName || undefined,
    dob: directorForm.dob ? formatDateYYYYMMDD(directorForm.dob) : undefined,
    nationality: directorForm.nationality || undefined,
    isDirector: isDirector(directorForm.position),
    isShareholder: isShareholder(directorForm.position),
    isPersonWithSignificantControl: directorForm.isPersonWithSignificantControl,
    email: directorForm.email || undefined,
    mobile: directorForm.mobile || undefined,
    chPersonalCode: directorForm.chPersonalCodeExist ? directorForm.chPersonalCode || null : null,
  });
};

export const directorFormToDirectorUpdateData = (directorForm: DirectorStepFormValues): DirectorUpdateData => {
  return new DirectorUpdateData({
    firstName: directorForm.firstName || undefined,
    middleNames: directorForm.middleNames || undefined,
    lastName: directorForm.lastName || undefined,
    dob: directorForm.dob ? formatDateYYYYMMDD(directorForm.dob) : undefined,
    nationality: directorForm.nationality || undefined,
    isDirector: isDirector(directorForm.position),
    isShareholder: isShareholder(directorForm.position),
    isPersonWithSignificantControl: directorForm.isPersonWithSignificantControl,
    email: directorForm.email || undefined,
    mobile: directorForm.mobile || undefined,
    suburbOfBirth: directorForm.suburbOfBirth || undefined,
    townOfBirth: directorForm.townOfBirth || undefined,
    stateOfBirth: directorForm.stateOfBirth || undefined,
    countryOfBirth: directorForm.countryOfBirth || undefined,
    tfn: directorForm.tfn || undefined,
    chPersonalCode: directorForm.chPersonalCodeExist ? directorForm.chPersonalCode || null : null,
    isCurrentAddressLessThanOneYear: directorForm.isCurrentAddressLessThanOneYear,
    previousAddressInUk: directorForm.previousAddressInUk,
  });
};

export const getOtherDirectorsPersonalCodes = (directors: DirectorData[], directorId?: string): string[] =>
  directors
    .filter((director) => director.id !== directorId)
    .map((director) => director.chPersonalCode)
    .filter((code): code is string => Boolean(code));

export const formatDirectorName = (director?: DirectorData): string => {
  if (!director) {
    return "";
  }

  let fullName = director.firstName;

  if (director.middleNames) {
    fullName = `${fullName} ${director.middleNames}`;
  }

  return `${fullName} ${director.lastName || ""}`;
};

export function useDirectorFormInitialValues(
  directorId?: string,
  isMainDirector: boolean = false,
): DirectorStepFormValues {
  const { directorStore, directorAddressStore, directorPositionsStore } = useServices();
  return useMemo(() => {
    return directorDataToDirectorForm(
      directorId ? directorStore.tryGetDirectorById(directorId) : undefined,
      directorId ? directorAddressStore.tryGetForDirector(directorId) : undefined,
      directorId ? directorAddressStore.tryGetForDirector(directorId, DirectorAddressKey.Previous) : undefined,
      getDirectorPosition(directorId, isMainDirector, directorPositionsStore),
    );
  }, [directorId]);
}
